"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MoreHorizontal, Plus, Mail, Shield, Clock, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { mockTeam, roleConfig } from "@/lib/mock-data";

const pendingInvites = [
  { email: "jamie@example.com", role: "member", sentAt: "2 days ago" },
  { email: "casey@example.com", role: "viewer", sentAt: "5 days ago" },
];

export default function TeamPage() {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-8 py-5 border-b bg-card">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Team</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {mockTeam.length} members &middot; {pendingInvites.length} pending invites
          </p>
        </div>
        <Button className="shadow-sm shadow-primary/25">
          <UserPlus className="h-4 w-4 mr-2" />
          Invite Member
        </Button>
      </div>

      <div className="flex-1 p-8 overflow-auto space-y-6">
        {/* Members */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Members</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-1">
              {mockTeam.map((member) => {
                const role = roleConfig[member.role];
                return (
                  <div
                    key={member.id}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-semibold">
                        {member.initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{member.name}</p>
                        <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full", role.bg, role.color)}>
                          {role.label}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{member.email}</p>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" />
                      {member.lastActive}
                    </div>
                    <Select defaultValue={member.role}>
                      <SelectTrigger className="w-[110px] h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="member">Member</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Pending Invites */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Pending Invites</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-1">
              {pendingInvites.map((invite) => {
                const role = roleConfig[invite.role];
                return (
                  <div
                    key={invite.email}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{invite.email}</p>
                      <p className="text-xs text-muted-foreground">Invited {invite.sentAt}</p>
                    </div>
                    <span className={cn("text-[11px] font-medium px-2 py-0.5 rounded-full", role.bg, role.color)}>
                      {role.label}
                    </span>
                    <Button variant="outline" size="sm" className="h-8 text-xs">
                      Resend
                    </Button>
                    <Button variant="ghost" size="sm" className="h-8 text-xs text-destructive hover:text-destructive">
                      Revoke
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Approval Workflow */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Approval Workflow</CardTitle>
              <Badge variant="secondary" className="text-xs">Active</Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/50">
              <Shield className="h-5 w-5 text-primary shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium">Content requires approval before publishing</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Posts created by Members must be approved by an Admin or Owner before they can be scheduled or published.
                </p>
              </div>
              <Button variant="outline" size="sm" className="h-8 text-xs shrink-0">
                Configure
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
